import React, { useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../common/Toast';

const MAX_FILES = 30;
const ACCEPTED_TYPES = 'image/*,video/mp4,video/quicktime,video/webm,video/*';

function isVideo(fileOrPath) {
  if (!fileOrPath) return false;
  const str = typeof fileOrPath === 'string' ? fileOrPath : (fileOrPath.name || fileOrPath.type || '');
  return /\.(mp4|mov|webm|avi|mkv)$/i.test(str) || /^video\//i.test(str);
}

export function PhotoUploader({ profileId, photoList, setPhotoList, previewList, setPreviewList }) {
  const fileRef = useRef();
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [failed, setFailed] = useState([]);

  async function handleFiles(files) {
    if (!files || files.length === 0) return;
    const remaining = MAX_FILES - photoList.length;
    const toUpload = Array.from(files).slice(0, remaining);
    if (toUpload.length === 0) return;

    setUploading(true);
    setFailed([]);
    const problems = [];

    for (let idx = 0; idx < toUpload.length; idx++) {
      const file = toUpload[idx];
      setUploadProgress(`Uploading ${idx + 1} of ${toUpload.length}…`);

      // Generate preview
      const dataUrl = await new Promise(res => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.readAsDataURL(file);
      });

      // Upload to Supabase storage
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${profileId}/${Date.now()}_${Math.random().toString(36).slice(2, 6)}.${ext}`;
      const { error } = await supabase.storage.from('evidence-photos').upload(path, file, { upsert: true });
      if (error) {
        // Keep going with the rest, but never let a failure pass unnoticed —
        // a missing photo is the difference between accepted and rejected evidence.
        problems.push(file.name || `file ${idx + 1}`);
        console.warn('Upload failed:', file.name, error.message);
        continue;
      }

      setPhotoList(prev => [...prev, path]);
      setPreviewList(prev => [...prev, dataUrl]);
    }

    setUploading(false);
    setUploadProgress('');
    if (problems.length > 0) {
      setFailed(problems);
      toast.error(`${problems.length} file${problems.length === 1 ? '' : 's'} didn't upload — please try again.`);
    }
    if (fileRef.current) fileRef.current.value = '';
  }

  function removeFile(idx) {
    setPhotoList(prev => prev.filter((_, i) => i !== idx));
    setPreviewList(prev => prev.filter((_, i) => i !== idx));
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8, marginBottom: 10 }}>
        {previewList.map((src, i) => {
          const video = isVideo(photoList[i]);
          return (
            <div key={i} style={{ position: 'relative', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)', aspectRatio: '1', background: 'var(--panel)' }}>
              {video ? (
                <video src={src} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} muted />
              ) : (
                <img src={src} alt={`File ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              )}
              {/* Number badge */}
              <div style={{ position: 'absolute', top: 0, left: 0, background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderBottomRightRadius: 4 }}>
                {i + 1}
              </div>
              {/* Video indicator */}
              {video && (
                <div style={{ position: 'absolute', bottom: 2, left: 2, background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 3 }}>
                  ▶ VID
                </div>
              )}
              {/* Remove button */}
              <button
                onClick={() => removeFile(i)}
                style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
              >×</button>
            </div>
          );
        })}

        {photoList.length < MAX_FILES && (
          <div
            onClick={() => !uploading && fileRef.current?.click()}
            style={{
              border: '2px dashed var(--border)', borderRadius: 6, aspectRatio: '1',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              cursor: uploading ? 'wait' : 'pointer', background: 'var(--bg)', gap: 4, minHeight: 90,
              opacity: uploading ? 0.6 : 1,
            }}
          >
            {uploading ? (
              <span style={{ fontSize: 10, color: 'var(--muted)', textAlign: 'center', padding: '0 4px' }}>{uploadProgress || 'Uploading…'}</span>
            ) : (
              <>
                <span style={{ fontSize: 22 }}>📷</span>
                <span style={{ fontSize: 10, color: 'var(--muted)', textAlign: 'center', padding: '0 4px' }}>
                  Add {photoList.length === 0 ? 'photos / videos' : 'more'}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED_TYPES}
        multiple
        style={{ display: 'none' }}
        onChange={e => handleFiles(e.target.files)}
      />

      {failed.length > 0 && (
        <div style={{ marginBottom: 8, padding: '8px 10px', borderRadius: 4, background: 'rgba(192,57,43,.08)', border: '1px solid rgba(192,57,43,.35)', fontSize: 11, color: 'var(--text)' }}>
          <strong style={{ color: 'var(--red)' }}>Didn't upload:</strong> {failed.join(', ')}. Tap "Add more" to try again.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: photoList.length === 0 ? 'var(--red)' : 'var(--muted)' }}>
          {photoList.length}/{MAX_FILES} files {photoList.length === 0 ? '— at least 1 required' : ''}
        </span>
        {photoList.length < MAX_FILES && photoList.length > 0 && !uploading && (
          <button
            className="btn-sm"
            style={{ fontSize: 10, padding: '3px 10px' }}
            onClick={() => fileRef.current?.click()}
          >
            + Add more
          </button>
        )}
      </div>
    </div>
  );
}
